import type { KeyboardEvent, MouseEvent } from 'react';

import { FromPriceDisplay } from '../PriceDisplay';
import {
  ActionButton,
  Content,
  Header,
  Image,
  ImageBadge,
  ImageWrapper,
  ItemCardBase,
  Meta,
  OverlayContent,
  OverlayPrice,
  OverlayTitle,
  Placeholder,
  PriceRow,
  StatusBadge,
  Title,
} from './ItemCard.styles';
import type { ItemCardProps } from './ItemCard.types';

export function ItemCard({
  image,
  imageAspectRatio,
  title,
  meta,
  price,
  statusBadge,
  imageBadge,
  action,
  customAction,
  unavailable,
  ariaLabel,
  variant = 'default',
  textPosition = 'left',
  boldTitle = false,
  onSelect,
}: ItemCardProps) {
  const clickable = Boolean(onSelect);

  const trigger = () => {
    if (unavailable) return;
    if (onSelect) onSelect();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      trigger();
    }
  };

  if (variant === 'overlay') {
    return (
      <ItemCardBase
        $unavailable={unavailable}
        $clickable={clickable}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? (unavailable ? -1 : 0) : undefined}
        aria-disabled={unavailable || undefined}
        aria-label={ariaLabel}
        onClick={clickable ? trigger : undefined}
        onKeyDown={clickable ? handleKeyDown : undefined}
      >
        <ImageWrapper $aspectRatio={imageAspectRatio}>
          {image.src ? (
            <Image src={image.src} alt={image.alt} />
          ) : (
            <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
          )}
          <OverlayContent>
            <OverlayTitle>{title}</OverlayTitle>
            {price && (
              <OverlayPrice>
                <FromPriceDisplay
                  amount={price.amount}
                  currencyCode={price.currencyCode}
                  label={price.label}
                  discount={price.discount}
                  locale={price.locale}
                  size="small"
                />
              </OverlayPrice>
            )}
          </OverlayContent>
        </ImageWrapper>
      </ItemCardBase>
    );
  }

  return (
    <ItemCardBase
      $unavailable={unavailable}
      $clickable={clickable}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? (unavailable ? -1 : 0) : undefined}
      aria-disabled={unavailable || undefined}
      aria-label={ariaLabel}
      onClick={clickable ? trigger : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
    >
      <ImageWrapper $aspectRatio={imageAspectRatio}>
        {image.src ? (
          <Image src={image.src} alt={image.alt} />
        ) : (
          <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
        )}
        {imageBadge && <ImageBadge>{imageBadge}</ImageBadge>}
      </ImageWrapper>

      <Content $textPosition={textPosition}>
        <Header>
          <Title $bold={boldTitle}>{title}</Title>
          {customAction}
          {action && (
            <ActionButton
              type="button"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                action.onClick();
              }}
            >
              {action.label}
            </ActionButton>
          )}
        </Header>

        {meta && <Meta>{meta}</Meta>}

        {statusBadge && <StatusBadge>{statusBadge}</StatusBadge>}

        {price && (
          <PriceRow>
            <FromPriceDisplay
              amount={price.amount}
              currencyCode={price.currencyCode}
              label={price.label}
              discount={price.discount}
              locale={price.locale}
              size="small"
            />
          </PriceRow>
        )}
      </Content>
    </ItemCardBase>
  );
}
